/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { Table, TableCell, TableLayoutType, TableRow, WidthType } from 'docx';
import { frameworks, getSubGroup, Group } from '@g17eco/core';
import {
  ArrayToTableCellType, cellToTableCell,
  DEFAULTS,
  paragraph, Styles
} from './document-structure';
import { Tags, UniversalTrackerPlain } from '../../types/universalTracker';
import { ReportData } from '../../types/reportData';
import { naturalSort } from '../../utils';
import { UtrvStatus } from '../../constants/status';

export const simpleTable = (data: TableRow[], size = 8640, columnWidths?: number[]) => {
  const firstRow = data[0];
  const cellCount = firstRow?.CellCount ?? 1;
  const defaultColumnWidths = Array.from({ length: cellCount }, () => size / cellCount);
  return new Table({
    rows: data,
    columnWidths: columnWidths ?? defaultColumnWidths,
    width: {
      size,
      type: WidthType.DXA,
    },
    layout: TableLayoutType.FIXED,
  })
}

interface NewGroupRow {
  group: Group | undefined;
  rowSpan: number;
  columnSpan: number;
  subGroupKey: string;
  defaultColour?: string;
}

export const createNewGroupRow = ({group, rowSpan, columnSpan, subGroupKey, defaultColour}: NewGroupRow) => {
  const subGroup = group ? getSubGroup(group, subGroupKey) : undefined;
  const subGroupName = subGroup ? subGroup.name : subGroupKey;

  return new TableRow({
    children: [
      new TableCell({
        children: [
          paragraph(group?.name ?? '', { style: 'plainWhite' })
        ],
        borders: DEFAULTS.TABLE_NO_BORDERS,
        shading: {
          fill: group?.colour ?? defaultColour ?? ''
        },
        rowSpan: rowSpan
      }),
      new TableCell({
        children: [
          paragraph(subGroupName ?? '')
        ],
        borders: DEFAULTS.TABLE_NO_BORDERS,
        shading: DEFAULTS.TABLE_HEADER_SHADING,
        columnSpan: columnSpan
      }),
    ]
  })
}

interface NewSubGroupRow {
  group: Group | undefined;
  subGroupKey: string;
  columnSpan: number;
}

export const createNewSubGroupRow = ({group, subGroupKey, columnSpan}: NewSubGroupRow) => {
  const subGroup = group ? getSubGroup(group, subGroupKey) : undefined;
  const subGroupName = subGroup ? subGroup.name : subGroupKey;

  return new TableRow({
    children: [
      new TableCell({
        children: [
          paragraph(subGroupName ?? '')
        ],
        borders: DEFAULTS.TABLE_NO_BORDERS,
        shading: DEFAULTS.TABLE_HEADER_SHADING,
        columnSpan: columnSpan
      }),
    ]
  })
}

export const createNoBorderCell = (cell: ArrayToTableCellType, styles?: Styles) => {
  return cellToTableCell(cell, { ...styles, borders: DEFAULTS.TABLE_NO_BORDERS });
}

export const getUtrvStatusText = (currentData: ReportData) => {
  switch (currentData.status) {
    case UtrvStatus.Created:
      return 'Not submitted';
    case UtrvStatus.Updated:
      return 'Waiting verification';
    case UtrvStatus.Rejected:
      return 'Rejected';
    case UtrvStatus.Verified:
    default:
      return 'Verified';
  }
};

export const getFrameworkTags = (tag: string, utr: UniversalTrackerPlain) => {
  const tagCodes = utr.tags?.[tag as keyof Tags];
  if (!tagCodes) {
    return '';
  }
  const frameworkMap = frameworks[tag];
  if (!frameworkMap) {
    return tagCodes.join(', ');
  }

  const getName = (code: string) => {
    const framework = frameworkMap.subgroups?.find(f => f.code === code);
    if (framework) {
      return framework.name;
    }
    return code;
  }
  const tagNames = tagCodes.map(getName)
  return tagNames.join(', ')
}

export const getTypeCode = (a: ReportData, key: string) => {
  if (a.universalTracker.alternatives?.[key]) {
    return a.universalTracker.alternatives?.[key]?.typeCode ?? '';
  }
  if (a.universalTracker.type === key) {
    return a.universalTracker.typeCode;
  }
  return '';
}

export const getAltName = (a: ReportData, key: string) => {
  if (a.universalTracker.type === key) {
    return a.universalTracker.name;
  }

  if (a.universalTracker.alternatives?.[key]) {
    return a.universalTracker.alternatives?.[key]?.name ?? '';
  }

  return '';
}

export const getTypeTags = (utr: UniversalTrackerPlain) => {
  const typeTags: string[] = [];
  if (Array.isArray(utr.typeTags)) {
    typeTags.push(...utr.typeTags)
  }

  if (utr.alternatives) {
    Object.values(utr.alternatives).forEach(alt => {
      if (alt.typeTags) {
        typeTags.push(...alt.typeTags)
      }
    })
  }

  return typeTags
}

type DataGroups = Record<string, Record<string, ReportData[]>>;

interface GroupData {
  topLevelGroup: Group;
  data: ReportData[];
  cellCount: number;
  createRow: (d: ReportData) => TableRow;
  defaultColour?: string;
}

export const createDataGroups = (topLevelGroup: Group, data: ReportData[]) => {
  const dataGroups: DataGroups = {};

  const subGroupTypes = topLevelGroup.subgroups?.map(g => g.code) ?? [];
  data.forEach(d => {
    const typeTags = getTypeTags(d.universalTracker);
    typeTags.filter(g => subGroupTypes.includes(g)).forEach(group => {
      if (!dataGroups[group]) {
        dataGroups[group] = {};
      }
      getSubGroup(topLevelGroup, group)?.subgroups?.forEach(g => {
        // Ensure utr has inner group code as tag
        if (typeTags.includes(g.code)) {
          if (!dataGroups[group][g.code]) {
            dataGroups[group][g.code] = [];
          }
          dataGroups[group][g.code].push(d);
        }
      })
    })
  });
  return dataGroups;
};

export const getGroupData = ({topLevelGroup, data, cellCount, createRow, defaultColour}: GroupData) => {
  const dataGroups = createDataGroups(topLevelGroup, data);

  const rows: TableRow[] = [];
  const columnSpan = cellCount - 1;

  Object.keys(dataGroups).sort().forEach(groupKey => {
    const subGroups = dataGroups[groupKey];
    const rowSpan = Object.keys(subGroups).reduce((acc, g) => acc + subGroups[g].length, 0) + Object.keys(subGroups).length;
    const group = getSubGroup(topLevelGroup, groupKey);

    Object.keys(subGroups).sort().forEach((subGroupKey, i) => {
      if (i === 0) {
        rows.push(createNewGroupRow({group, rowSpan, subGroupKey, columnSpan, defaultColour}));
      } else {
        rows.push(createNewSubGroupRow({group, subGroupKey, columnSpan }));
      }
      const d = subGroups[subGroupKey];
      d.sort((a, b) => naturalSort(getTypeCode(a, topLevelGroup.code), getTypeCode(b, topLevelGroup.code)))
        .forEach(r => rows.push(createRow(r)));
    });
  });

  return rows;
}

export const getLatestNote = (currentData: ReportData, emptyText = '') => {
  const { history = [], note, notes } = currentData;

  if (note) {
    return note;
  }

  if (notes?.stakeholder?.note) {
    return notes.stakeholder.note;
  }

  // @TODO should remove it and rely on utrv.notes property
  for (let i = history.length - 1; i >= 0; i--) {
    const h = history[i];
    const action = h.action as UtrvStatus;

    if (action === UtrvStatus.Updated) {
      return h.note || emptyText;
    }
  }

  return emptyText;
};
